/**
 * GolferProfileEditor
 *
 * RPG-style skill editor for creating and modifying golfer profiles.
 * Allows adjustment of handicap, skill breakdown, consistency, and situational factors.
 */

import React, { useState } from 'react';
import {
    SKILL_LABELS,
    getSkillDescription,
    getHandicapCategory,
    GOLFER_PRESETS
} from '../utility/golferProfiles';
import { SkillRadarChart, SkillRadarChartCompact } from './SkillRadarChart';

/**
 * Skill slider component with visual indicator
 */
const SkillSlider = ({ label, value, onChange, min = -0.8, max = 0.8, step = 0.05 }) => {
    const percentage = ((value - min) / (max - min)) * 100;
    const description = getSkillDescription(value);

    // Colour based on value
    const getColour = (val) => {
        if (val >= 0.3) return '#22C55E';  // Green
        if (val >= 0.1) return '#84CC16';  // Lime
        if (val >= -0.1) return '#6B7280'; // Grey
        if (val >= -0.3) return '#F59E0B'; // Amber
        return '#EF4444';                   // Red
    };

    return (
        <div className="mb-4">
            <div className="flex justify-between items-center mb-1">
                <span className="text-sm font-medium text-gray-700">{label}</span>
                <span
                    className="text-xs font-semibold px-2 py-0.5 rounded"
                    style={{ backgroundColor: getColour(value), color: 'white' }}
                >
                    {description}
                </span>
            </div>
            <div className="relative">
                <input
                    type="range"
                    min={min}
                    max={max}
                    step={step}
                    value={value}
                    onChange={(e) => onChange(parseFloat(e.target.value))}
                    className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                    style={{
                        background: `linear-gradient(to right, #62A87C ${percentage}%, #E5E7EB ${percentage}%)`
                    }}
                />
                {/* Centre marker for neutral */}
                <div
                    className="absolute top-0 w-0.5 h-2 bg-gray-400"
                    style={{ left: '50%', transform: 'translateX(-50%)' }}
                />
            </div>
            <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>Weak</span>
                <span>Average</span>
                <span>Strong</span>
            </div>
        </div>
    );
};

/**
 * Simple slider for non-skill attributes
 */
const AttributeSlider = ({ label, value, onChange, min, max, step, format, description }) => {
    const percentage = ((value - min) / (max - min)) * 100;

    return (
        <div className="mb-4">
            <div className="flex justify-between items-center mb-1">
                <span className="text-sm font-medium text-gray-700">{label}</span>
                <span className="text-sm text-gray-600">{format ? format(value) : value}</span>
            </div>
            {description && (
                <p className="text-xs text-gray-500 mb-2">{description}</p>
            )}
            <input
                type="range"
                min={min}
                max={max}
                step={step}
                value={value}
                onChange={(e) => onChange(parseFloat(e.target.value))}
                className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                style={{
                    background: `linear-gradient(to right, #62A87C ${percentage}%, #E5E7EB ${percentage}%)`
                }}
            />
        </div>
    );
};

/**
 * Collapsible section
 */
const CollapsibleSection = ({ title, children, defaultOpen = false }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    return (
        <div className="border border-gray-200 rounded-lg mb-4">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full px-4 py-3 flex justify-between items-center bg-gray-50 hover:bg-gray-100 rounded-t-lg"
            >
                <span className="font-medium text-gray-700">{title}</span>
                <span className="text-gray-400">{isOpen ? '−' : '+'}</span>
            </button>
            {isOpen && (
                <div className="p-4">
                    {children}
                </div>
            )}
        </div>
    );
};


/**
 * Main profile editor component
 */
export const GolferProfileEditor = ({ golfer, onChange, compact = false }) => {
    const [activeTab, setActiveTab] = useState('skills');

    const handleSkillChange = (skillKey, value) => {
        onChange({
            ...golfer,
            skills: {
                ...golfer.skills,
                [skillKey]: value
            }
        });
    };

    const handleSituationalChange = (key, value) => {
        onChange({
            ...golfer,
            situational: {
                ...golfer.situational,
                [key]: value
            }
        });
    };

    const handlePresetSelect = (presetKey) => {
        const preset = GOLFER_PRESETS[presetKey];
        if (preset) {
            onChange({
                ...preset,
                id: golfer.id,  // Keep original ID
                name: golfer.name || preset.name  // Keep name if set
            });
        }
    };

    const handicapCategory = getHandicapCategory(golfer.handicapIndex);

    if (compact) {
        return (
            <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                    <input
                        type="text"
                        value={golfer.name}
                        onChange={(e) => onChange({ ...golfer, name: e.target.value })}
                        className="text-lg font-semibold border-b border-transparent hover:border-gray-300 focus:border-green-500 focus:outline-none"
                        placeholder="Golfer Name"
                    />
                    <SkillRadarChartCompact
                        skills={golfer.skills}
                        handicap={golfer.handicapIndex}
                        consistency={golfer.consistency}
                        size={70}
                    />
                </div>

                <AttributeSlider
                    label="Handicap"
                    value={golfer.handicapIndex}
                    onChange={(v) => onChange({ ...golfer, handicapIndex: Math.round(v) })}
                    min={-5}
                    max={36}
                    step={1}
                    format={(v) => `${v} (${getHandicapCategory(v)})`}
                />

                <div className="grid grid-cols-2 gap-2 mt-3">
                    {Object.entries(SKILL_LABELS).map(([key, label]) => (
                        <div key={key} className="text-xs">
                            <span className="text-gray-500">{label}:</span>
                            <span
                                className="ml-1 font-medium"
                                style={{ color: golfer.skills[key] >= 0 ? '#22C55E' : '#EF4444' }}
                            >
                                {golfer.skills[key] >= 0 ? '+' : ''}{golfer.skills[key].toFixed(2)}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
            {/* Header */}
            <div className="p-4 border-b border-gray-200">
                <div className="flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                        <input
                            type="text"
                            value={golfer.name}
                            onChange={(e) => onChange({ ...golfer, name: e.target.value })}
                            className="text-xl font-bold border-b-2 border-transparent hover:border-gray-300 focus:border-green-500 focus:outline-none w-full truncate"
                            placeholder="Golfer Name"
                        />
                        <p className="text-sm text-gray-500 mt-1">{handicapCategory}</p>
                    </div>
                    <div className="flex-shrink-0">
                        <SkillRadarChart
                            skills={golfer.skills}
                            handicap={golfer.handicapIndex}
                            consistency={golfer.consistency}
                            size={160}
                        />
                    </div>
                </div>

                {/* Preset selector */}
                <div className="mt-4">
                    <label className="text-xs text-gray-500 block mb-1">Load Preset:</label>
                    <select
                        onChange={(e) => handlePresetSelect(e.target.value)}
                        className="w-full text-sm border border-gray-200 rounded px-2 py-1"
                        defaultValue=""
                    >
                        <option value="" disabled>Select a preset...</option>
                        {Object.entries(GOLFER_PRESETS).map(([key, preset]) => (
                            <option key={key} value={key}>
                                {preset.name} (Hcp {preset.handicapIndex})
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Tab navigation */}
            <div className="flex border-b border-gray-200">
                {['skills', 'attributes', 'situational'].map(tab => (
                    <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={`flex-1 py-2 text-sm font-medium ${activeTab === tab
                                ? 'text-green-600 border-b-2 border-green-600'
                                : 'text-gray-500 hover:text-gray-700'
                            }`}
                    >
                        {tab.charAt(0).toUpperCase() + tab.slice(1)}
                    </button>
                ))}
            </div>

            {/* Tab content */}
            <div className="p-4">
                {activeTab === 'skills' && (
                    <div>
                        <p className="text-sm text-gray-600 mb-4">
                            Skill modifiers adjust performance relative to handicap baseline.
                            Positive values indicate strength; negative values indicate weakness.
                        </p>
                        {Object.entries(SKILL_LABELS).map(([key, label]) => (
                            <SkillSlider
                                key={key}
                                label={label}
                                value={golfer.skills[key]}
                                onChange={(v) => handleSkillChange(key, v)}
                            />
                        ))}
                    </div>
                )}

                {activeTab === 'attributes' && (
                    <div>
                        <AttributeSlider
                            label="Handicap Index"
                            value={golfer.handicapIndex}
                            onChange={(v) => onChange({ ...golfer, handicapIndex: Math.round(v) })}
                            min={-5}
                            max={36}
                            step={1}
                            format={(v) => `${v}`}
                            description="Overall ability level. Lower = better golfer."
                        />

                        <AttributeSlider
                            label="Consistency"
                            value={golfer.consistency}
                            onChange={(v) => onChange({ ...golfer, consistency: v })}
                            min={0.7}
                            max={1.4}
                            step={0.05}
                            format={(v) => v < 0.9 ? 'Very Consistent' : v < 1.1 ? 'Average' : 'Volatile'}
                            description="Lower values = more consistent scores. Higher values = more variance."
                        />

                        <div className="mt-4">
                            <label className="text-sm font-medium text-gray-700 block mb-2">
                                Nationality
                            </label>
                            <select
                                value={golfer.nationality}
                                onChange={(e) => onChange({ ...golfer, nationality: e.target.value })}
                                className="w-full border border-gray-200 rounded px-3 py-2"
                            >
                                {['USA', 'GBR', 'SCO', 'IRL', 'NIR', 'WAL', 'AUS', 'RSA', 'ESP', 'GER', 'FRA', 'ITA', 'SWE', 'JPN', 'KOR', 'CAN', 'ARG', 'MEX', 'NZL', 'NOR', 'DEN'].map(nat => (
                                    <option key={nat} value={nat}>{nat}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                )}

                {activeTab === 'situational' && (
                    <div>
                        <p className="text-sm text-gray-600 mb-4">
                            Situational factors affect performance in specific conditions.
                        </p>

                        <AttributeSlider
                            label="Weather Resilience"
                            value={golfer.situational.weatherResilience}
                            onChange={(v) => handleSituationalChange('weatherResilience', v)}
                            min={0}
                            max={1}
                            step={0.05}
                            format={(v) => v < 0.3 ? 'Struggles' : v < 0.6 ? 'Average' : 'Thrives'}
                            description="Ability to perform in wind and rain."
                        />

                        <AttributeSlider
                            label="Pressure Handling"
                            value={golfer.situational.pressureHandling}
                            onChange={(v) => handleSituationalChange('pressureHandling', v)}
                            min={-0.5}
                            max={0.5}
                            step={0.05}
                            format={(v) => v < -0.2 ? 'Chokes' : v < 0.2 ? 'Neutral' : 'Clutch'}
                            description="Performance when in contention. Negative = chokes under pressure."
                        />

                        <AttributeSlider
                            label="Home Advantage"
                            value={golfer.situational.homeAdvantage}
                            onChange={(v) => handleSituationalChange('homeAdvantage', v)}
                            min={0}
                            max={0.5}
                            step={0.05}
                            format={(v) => `${(v * 2).toFixed(1)} strokes`}
                            description="Bonus when playing in home country."
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

/**
 * Compact golfer card for lists
 */
export const GolferCard = ({ golfer, onSelect, selected = false }) => {
    const category = getHandicapCategory(golfer.handicapIndex);

    return (
        <div
            onClick={onSelect}
            className={`p-3 border rounded-lg cursor-pointer transition-all ${selected
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                }`}
        >
            <div className="flex items-center justify-between">
                <div>
                    <p className="font-medium text-gray-800">{golfer.name}</p>
                    <p className="text-xs text-gray-500">{category} • {golfer.nationality}</p>
                </div>
                <SkillRadarChartCompact
                    skills={golfer.skills}
                    handicap={golfer.handicapIndex}
                    consistency={golfer.consistency}
                    size={70}
                />
            </div>

            {/* Skill bars */}
            <div className="mt-2 grid grid-cols-4 gap-1">
                {Object.entries(golfer.skills).map(([key, value]) => (
                    <div key={key} className="text-center">
                        <div
                            className="h-1 rounded-full mx-auto"
                            style={{
                                width: '80%',
                                backgroundColor: value >= 0.2 ? '#22C55E'
                                    : value >= -0.2 ? '#6B7280'
                                        : '#EF4444'
                            }}
                        />
                        <span className="text-[10px] text-gray-400 mt-0.5 block">
                            {SKILL_LABELS[key]?.split(' ')[0] || key}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default GolferProfileEditor;
